/**
 * Posts API — typed wrappers around /api/posts.
 *
 * Multipart create uses FormData directly so the auth helper stays JSON-only.
 */
import { auth } from '../firebase';
import { api, ApiError } from './api';

export type PostCategory =
  | 'police_activity' | 'shooting' | 'fire_ems' | 'traffic' | 'pursuit'
  | 'containment' | 'suspicious' | 'community' | 'other'
  // Community + commerce categories. eventStartTs / eventEndTs are REQUIRED
  // for these four — backend rejects them without a time window.
  | 'garage_sale' | 'event_small' | 'event_big' | 'business';

export type PostStatus = 'live' | 'archived' | 'hidden' | 'deleted';

export interface PostAuthor {
  uid: string;
  displayName?: string;
  photoURL?: string;
  username?: string;
  neighborhood?: string;
  role: 'user' | 'trusted_source' | 'admin';
  trustedReporter: boolean;
  postCount: number;
  createdAt: number;
}

export interface PostMedia {
  url: string;
  kind: 'image' | 'video';
  thumb: string | null;
}

export interface PostAttribution {
  url: string;
  label: string | null;
}

export interface Corroboration {
  eventId: number;
  reporterCount: number | null;
  inCad: boolean | null;
  notInCad: boolean;
}

export interface Promotion {
  id: number;
  endsAt: number;
  active: boolean;
}

export interface Post {
  id: number;
  category: PostCategory;
  title: string;
  description: string;
  threatLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  location: { lat: number; lng: number } | null;
  address: string | null;
  media: PostMedia | null;
  attribution: PostAttribution | null;
  upvotes: number;
  downvotes: number;
  reportCount: number;
  status: PostStatus;
  createdAt: number;
  archivedAt: number | null;
  eventStartTs: number | null; // unix seconds; required for garage_sale/event_*/business
  eventEndTs:   number | null;
  author: PostAuthor | null;
  myVote: 1 | -1 | null;
  corroboration: Corroboration | null;
  promotion: Promotion | null;
}

export interface CorroboratedEvent {
  id: number;
  category: PostCategory | string;
  location: { lat: number; lng: number };
  startedAt: number;
  lastActivityAt: number;
  postCount: number;
  reporterCount: number;
  inCad: boolean;
  notInCad: boolean;
  status: 'active' | 'closed';
  closedAt: number | null;
}

export interface PostMapMarker {
  id: number;
  uid: string;
  category: PostCategory;
  title: string;
  lat: number;
  lon: number;
  created_at: number;
  status: PostStatus;
  upvotes: number;
  downvotes: number;
}

export const DOWNVOTE_REASONS = ['spam', 'false', 'off_topic', 'duplicate'] as const;
export const REPORT_REASONS   = ['spam', 'false', 'off_topic', 'harassment', 'threat', 'nsfw', 'illegal', 'other'] as const;
export type DownvoteReason = typeof DOWNVOTE_REASONS[number];
export type ReportReason   = typeof REPORT_REASONS[number];

export interface CreatePostInput {
  category: PostCategory;
  title: string;
  description: string;
  lat?: number;
  lng?: number;
  media?: File | null;
  attributionUrl?: string;
  attributionLabel?: string;
}

export interface CreatePostResult {
  post: Post;
  moderation: { flagged: boolean; tier: number | null; hidden: boolean };
  corroboration: Corroboration | null;
}

async function authedFormFetch(path: string, form: FormData): Promise<Response> {
  const headers: Record<string, string> = {};
  const u = auth.currentUser;
  if (u) {
    try { headers['Authorization'] = `Bearer ${await u.getIdToken()}`; } catch {}
  }
  return fetch(path, { method: 'POST', body: form, headers });
}

export const postsApi = {
  categories: () => api<{
    categories: PostCategory[];
    downvoteReasons: DownvoteReason[];
    reportReasons: ReportReason[];
  }>('/api/posts/categories'),

  feed: (opts: { category?: PostCategory; before?: number; limit?: number; archived?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.category) q.set('category', opts.category);
    if (opts.before)   q.set('before', String(opts.before));
    if (opts.limit)    q.set('limit', String(opts.limit));
    if (opts.archived) q.set('archived', '1');
    const qs = q.toString();
    return api<{ posts: Post[] }>(`/api/posts/feed${qs ? `?${qs}` : ''}`);
  },

  map: (lat: number, lon: number, radiusKm = 10, limit = 200) =>
    api<{ markers: PostMapMarker[] }>(
      `/api/posts/map?lat=${lat}&lon=${lon}&radius_km=${radiusKm}&limit=${limit}`,
    ),

  detail: (id: number) => api<{ post: Post }>(`/api/posts/${id}`),

  async create(input: CreatePostInput): Promise<CreatePostResult> {
    const form = new FormData();
    form.append('category', input.category);
    form.append('title', input.title);
    form.append('description', input.description);
    if (input.lat != null && input.lng != null) {
      form.append('lat', String(input.lat));
      form.append('lon', String(input.lng));
    }
    if (input.media) form.append('media', input.media);
    if (input.attributionUrl) form.append('attributionUrl', input.attributionUrl);
    if (input.attributionLabel) form.append('attributionLabel', input.attributionLabel);
    const res = await authedFormFetch('/api/posts', form);
    let body: any = null;
    try { body = await res.json(); } catch {}
    if (!res.ok) throw new ApiError(body?.error || res.statusText, res.status, body);
    return {
      post: body.post as Post,
      moderation: body.moderation ?? { flagged: false, tier: null, hidden: false },
      corroboration: (body.corroboration ?? null) as Corroboration | null,
    };
  },

  vote: (id: number, value: 1 | -1, reason?: DownvoteReason) =>
    api<{ id: number; upvotes: number; downvotes: number; myVote: 1 | -1 }>(
      `/api/posts/${id}/vote`,
      { method: 'POST', json: { value, reason }, requireAuth: true },
    ),

  report: (id: number, reason: ReportReason, detail?: string) =>
    api<{ id: number; reportCount: number }>(
      `/api/posts/${id}/report`,
      { method: 'POST', json: { reason, detail }, requireAuth: true },
    ),

  delete: (id: number) =>
    api<{ id: number; deleted: boolean }>(`/api/posts/${id}`, { method: 'DELETE', requireAuth: true }),
  promote: (id: number, hours = 24, paymentRef?: string) =>
    api<{ id: number; postId: number; endsAt: number }>(`/api/posts/${id}/promote`, {
      method: 'POST', json: { hours, paymentRef }, requireAuth: true,
    }),
  unpromote: (id: number) =>
    api<{ id: number; cancelled: number }>(`/api/posts/${id}/promote`, { method: 'DELETE', requireAuth: true }),
};

export const eventsApi = {
  list: (status: 'active' | 'closed' = 'active', limit = 20) =>
    api<{ events: CorroboratedEvent[] }>(`/api/events?status=${status}&limit=${limit}`),
  detail: (id: number) =>
    api<{ event: CorroboratedEvent; posts: Post[]; reporters: PostAuthor[] }>(`/api/events/${id}`),
};

export interface TrustedSourceApplication {
  id: number;
  uid: string;
  organization: string;
  contactEmail: string;
  contactPhone: string | null;
  roleDescription: string;
  whyTrusted: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  reviewedBy: string | null;
  reviewedAt: number | null;
  reviewNotes: string | null;
  createdAt: number;
}

export interface TrustedSourceApplyInput {
  organization: string;
  contactEmail: string;
  contactPhone?: string;
  roleDescription: string;
  whyTrusted: string;
}

export const trustedSourceApi = {
  myApplication: () => api<{ application: TrustedSourceApplication | null }>(
    '/api/users/me/trusted-source', { requireAuth: true },
  ),
  apply: (input: TrustedSourceApplyInput) => api<{ applicationId: number; status: 'pending' }>(
    '/api/users/me/trusted-source', { method: 'POST', json: input, requireAuth: true },
  ),
  withdraw: () => api<{ withdrawn: boolean }>(
    '/api/users/me/trusted-source', { method: 'DELETE', requireAuth: true },
  ),
};
